import React, { useState } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../Contexts/ProfileContext';
import {
  getGoogleOAuthClientId,
  isGoogleSignInConfigured,
  signInWithGoogleIdToken,
} from '../utils/socialAuth';
import './GoogleSignInButton.scss';

/**
 * Styled "Continue with Google" using GIS (id_token) + custom route on API.
 * @param {'web'|'mob'} variant
 * @param {(msg: string) => void} [onError]
 * @param {boolean} [disabled]
 */
export function GoogleSignInButton({ variant = 'web', onError, disabled = false }) {
  const navigate = useNavigate();
  const { fetchProfile } = useProfile();
  const [loading, setLoading] = useState(false);

  const clientId = getGoogleOAuthClientId();

  const handleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      onError?.('Google sign-in did not return a credential.');
      return;
    }

    setLoading(true);
    try {
      await signInWithGoogleIdToken(idToken);
      await fetchProfile(true);
      navigate('/');
    } catch (err) {
      const msg =
        err.response?.data?.msg ||
        err.response?.data?.message ||
        err.message ||
        'Google sign-in failed. Please try again.';
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    onError?.('Google sign-in was cancelled or failed.');
  };

  if (!isGoogleSignInConfigured()) {
    return (
      <button
        type="button"
        className={variant === 'mob' ? 'mob-oauth-btn mob-oauth-btn--google' : 'auth-social-btn auth-social-btn-google'}
        disabled
        title="Set REACT_APP_GOOGLE_CLIENT_ID in .env"
      >
        {variant === 'mob' ? (
          <>
            <i className="bi bi-google" aria-hidden />
            <span>Google (not configured)</span>
          </>
        ) : (
          <>Continue with Google (not configured)</>
        )}
      </button>
    );
  }

  const visualClass =
    variant === 'mob'
      ? 'mob-oauth-btn mob-oauth-btn--google'
      : 'auth-social-btn auth-social-btn-google';

  return (
    <div
      className={`google-sign-in-button google-sign-in-button--${variant}${loading ? ' google-sign-in-button--loading' : ''}`}
      aria-busy={loading}
    >
      <button type="button" className={visualClass} disabled={disabled || loading} tabIndex={-1}>
        {variant === 'mob' ? (
          <>
            <i className="bi bi-google" aria-hidden />
            <span>{loading ? 'Signing in…' : 'Continue with Google'}</span>
          </>
        ) : (
          <>
            <svg className="auth-social-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden>
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {loading ? 'Signing in…' : 'Continue with Google'}
          </>
        )}
      </button>
      <div className="google-sign-in-button__gsi" aria-hidden={loading}>
        <GoogleLogin
          onSuccess={handleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          theme="outline"
          size="large"
          text="continue_with"
          shape="rectangular"
          width="320"
          locale="en"
        />
      </div>
    </div>
  );
}

/** Wrap app when REACT_APP_GOOGLE_CLIENT_ID is set. */
export function GoogleOAuthProviderWrapper({ children }) {
  const clientId = getGoogleOAuthClientId();
  if (!clientId) return children;
  return <GoogleOAuthProvider clientId={clientId}>{children}</GoogleOAuthProvider>;
}
